import { createApp } from 'vue';
import * as Sentry from '@sentry/vue';

import '@/assets/styles/main.scss';

import App from './App.vue';
import router from './router';
import vuetify from '@/plugins/vuetify';
import i18n from '@/plugins/vueI18n';
import vueQueryPluginOptions from '@/plugins/vueQueryPlugin';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { version } from '../package.json';

import '@/helpers/polyfills';
import { clearChunkRecoveryGuard } from '@/helpers/chunkRecovery';

const app = createApp(App);

app.use(i18n);
app.use(vuetify);
app.use(router);
app.use(VueQueryPlugin, vueQueryPluginOptions);

if (
  import.meta.env.VITE_SENTRY_ENABLE === 'true' ||
  import.meta.env.VITE_SENTRY_ENABLE === '1' ||
  import.meta.env.VITE_SENTRY_ENABLE === 1 ||
  import.meta.env.VITE_SENTRY_ENABLE === true
) {
  Sentry.init({
    app,
    release: `webmail-gui@v${version}`,
    environment: import.meta.env.VITE_WEBMAIL_ENVIRONMENT,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        useCompression: false,
        unmask: ['[sentry-unmask]']
      })
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.5),

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets:
      import.meta.env.VITE_SENTRY_TRACE_PROPAGATION_TARGETS?.split(',') ?? [],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Percentage chance a given sampled transaction will be profiled
    profilesSampleRate: Number(import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE ?? 0.5),
    beforeSend: (event) => {
      if (event.transaction === 'Settings2FA' && event.exception?.values?.[0]?.value) {
        return null;
      }

      // Filter out network errors from Axios
      const errorMessage = event.exception?.values?.[0]?.value;
      if (errorMessage === 'Network Error') {
        return null;
      }

      return event;
    }
  });
}

app.mount('#app');

// A stable mount well after a chunk-recovery reload is itself proof the recovery worked.
setTimeout(clearChunkRecoveryGuard, 10_000);
